// Browser-side Gemini model preflight + fallback walk. Mirrors the desktop app's
// app/backend/models/gemini.js semantics (getAccessibleModels cached 10 min per key,
// resolveAccessibleModel requested → default → first accessible fallback, fail-open
// on network failure, KEY_INVALID thrown fast and never retried).
//
// Why: gemini-2.5-flash is retiring (~Oct 2026) and new free AI Studio keys already
// 404 on it. Instead of sending a blind model id to generateContent, list the key's
// accessible models first and land on one that actually works.

import { CONFIG } from '../../config.js';

const ACCESSIBLE_MODELS_TTL_MS = 10 * 60 * 1000;
const accessibleModelsCache = new Map(); // apiKey -> { at: epoch-ms, models: Set<string> }

// Key-level errors: no model will work — surface fast, never walk fallbacks.
const KEY_ERROR_RE = /api[\s_]?key (?:expired|not valid|invalid|blocked|revoked)|expired\. please renew|API_KEY_INVALID|PERMISSION_DENIED/i;
// Model-level errors: this model id is dead/inaccessible — walking fallbacks helps.
const MODEL_ERROR_RE = /\b404\b|not found|is not supported|not supported for/i;

export function isKeyError(msg) {
  return KEY_ERROR_RE.test(String(msg || ''));
}

export function isModelError(msg) {
  const m = String(msg || '');
  return !isKeyError(m) && MODEL_ERROR_RE.test(m);
}

function keyInvalidError(msg) {
  const e = new Error(`API key Gemini bermasalah (${msg}). Cek atau buat key baru di Google AI Studio (aistudio.google.com/apikey).`);
  e.code = 'KEY_INVALID';
  return e;
}

/**
 * List the model ids accessible with this key (cached 10 min per key).
 * @returns {Promise<Set<string>|null>} null = listing unavailable (fail open)
 * @throws {Error & {code:'KEY_INVALID'}} on 400/401/403 (dead key)
 */
export async function getAccessibleModels(apiKey) {
  const hit = accessibleModelsCache.get(apiKey);
  if (hit && Date.now() - hit.at < ACCESSIBLE_MODELS_TTL_MS) return hit.models;
  let res;
  try {
    res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models?key=${encodeURIComponent(apiKey)}&pageSize=1000`,
      { signal: AbortSignal.timeout(10000) }
    );
  } catch {
    return null; // network failure → fail open (caller proceeds un-preflighted)
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = body.error?.message || `Gemini ListModels HTTP ${res.status}`;
    // 400/401/403 here are KEY-level problems (invalid / expired / blocked) —
    // no model will work, so surface immediately.
    if ([400, 401, 403].includes(res.status)) throw keyInvalidError(msg);
    return null; // 5xx etc → fail open
  }
  const data = await res.json().catch(() => ({}));
  const models = new Set((data.models || []).map((m) => String(m.name || '').replace('models/', '')));
  accessibleModelsCache.set(apiKey, { at: Date.now(), models });
  return models;
}

/**
 * Resolve the model to actually use for this key:
 * requested → CONFIG.GEMINI_MODEL → first accessible id in CONFIG.GEMINI_FALLBACKS.
 * Fails open (returns the requested/pinned id) when the listing can't be fetched.
 * @throws {Error & {code:'KEY_INVALID'}} when the key itself is dead
 */
export async function resolveAccessibleModel(apiKey, wanted) {
  const want = wanted && String(wanted).trim() ? String(wanted).trim() : CONFIG.GEMINI_MODEL;
  const models = await getAccessibleModels(apiKey); // KEY_INVALID propagates
  if (!models || models.size === 0) return want; // listing unavailable → fail open
  if (models.has(want)) return want;
  if (models.has(CONFIG.GEMINI_MODEL)) {
    console.warn(`[gemini] "${want}" not accessible with this key — using ${CONFIG.GEMINI_MODEL}`);
    return CONFIG.GEMINI_MODEL;
  }
  const fallback = CONFIG.GEMINI_FALLBACKS.find((id) => models.has(id));
  if (fallback) {
    console.warn(`[gemini] "${want}" and default not accessible with this key — using ${fallback}`);
    return fallback;
  }
  return want; // no variant overlap → fail open
}

/**
 * Run a generateContent-style call with the resolved model, walking
 * CONFIG.GEMINI_FALLBACKS on model-level errors (404 / not found / not supported).
 * Key-level errors (API_KEY_INVALID / expired / blocked / PERMISSION_DENIED) throw
 * immediately — retrying a dead key on other models just wastes the user's time.
 *
 * @param {string} apiKey
 * @param {string} wanted - the user's picked model (falls back to CONFIG.GEMINI_MODEL)
 * @param {(modelId: string) => Promise<any>} run - performs the actual call
 * @returns {Promise<{model: string, result: any}>}
 */
export async function generateWithFallback(apiKey, wanted, run) {
  let first = wanted && String(wanted).trim() ? String(wanted).trim() : CONFIG.GEMINI_MODEL;
  try {
    first = await resolveAccessibleModel(apiKey, first);
  } catch (e) {
    if (e.code === 'KEY_INVALID') throw e; // dead key — every model fails identically
    // any other preflight hiccup → fail open with the requested model
  }
  const candidates = [...new Set([first, ...CONFIG.GEMINI_FALLBACKS])];
  let lastErr = null;
  for (const modelId of candidates) {
    try {
      const result = await run(modelId);
      return { model: modelId, result };
    } catch (e) {
      const msg = e?.message || String(e);
      if (isKeyError(msg)) throw keyInvalidError(msg);
      if (!isModelError(msg)) throw e; // rate limit / network / safety — not fixable by switching model
      console.warn(`[gemini] model "${modelId}" gagal (${msg}) — coba fallback berikutnya`);
      lastErr = e;
    }
  }
  throw lastErr || new Error('Gemini: tidak ada model yang bisa dipakai dengan key ini.');
}
